import { useState, useEffect } from 'react';
import { collection, doc, onSnapshot, updateDoc, addDoc, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { Vote, VotingSession, UserRole } from '../types';

export const useVoting = (roomId: string, issueId?: string) => {
  const [votingSession, setVotingSession] = useState<VotingSession | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!issueId) {
      setVotingSession(null);
      setLoading(false);
      return;
    }

    const q = query(
      collection(db, 'votingSessions'),
      where('roomId', '==', roomId),
      where('issueId', '==', issueId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const sessionData = snapshot.docs[0].data() as VotingSession;
        sessionData.id = snapshot.docs[0].id;
        setVotingSession(sessionData);
      } else {
        setVotingSession(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [roomId, issueId]);

  const createVotingSession = async (issueId: string) => {
    const sessionData = {
      roomId,
      issueId,
      votes: [],
      isRevealed: false
    };

    await addDoc(collection(db, 'votingSessions'), sessionData);
  };

  const submitVote = async (userId: string, role: UserRole, points: number) => {
    if (!votingSession) return;

    const existingVoteIndex = votingSession.votes.findIndex(v => v.userId === userId);
    const newVotes = [...votingSession.votes];

    if (existingVoteIndex >= 0) {
      newVotes[existingVoteIndex] = { userId, role, points };
    } else {
      newVotes.push({ userId, role, points });
    }

    await updateDoc(doc(db, 'votingSessions', votingSession.id), {
      votes: newVotes
    });
  };

  const revealVotes = async () => {
    if (!votingSession) return;

    await updateDoc(doc(db, 'votingSessions', votingSession.id), {
      isRevealed: true
    });
  };

  const calculateFinalPoints = (votes: Vote[]): number => {
    const roleGroups: { [key in UserRole]?: number[] } = {};
    
    votes.forEach(vote => {
      if (!roleGroups[vote.role]) {
        roleGroups[vote.role] = [];
      }
      roleGroups[vote.role]!.push(vote.points);
    });

    const roleAverages: number[] = [];
    
    Object.entries(roleGroups).forEach(([role, points]) => {
      if (points!.length === 1) {
        roleAverages.push(points![0]);
      } else {
        const uniquePoints = Array.from(new Set(points!));
        if (uniquePoints.length === 1) {
          roleAverages.push(uniquePoints[0]);
        } else {
          const average = points!.reduce((sum, p) => sum + p, 0) / points!.length;
          roleAverages.push(Math.round(average));
        }
      }
    });

    return roleAverages.reduce((sum, avg) => sum + avg, 0);
  };

  const finalizeVote = async () => {
    if (!votingSession) return;

    const finalPoints = calculateFinalPoints(votingSession.votes);
    
    await updateDoc(doc(db, 'votingSessions', votingSession.id), {
      finalPoints
    });

    return finalPoints;
  };

  return {
    votingSession,
    loading,
    createVotingSession,
    submitVote,
    revealVotes,
    finalizeVote,
    calculateFinalPoints
  };
};